import clsx from 'clsx';
import { FC, ReactNode } from 'react';
import { z } from 'zod';

import styles from './PriceDisplay.module.scss';

export const PRICE_CHANGE_STATUSES = [
  'default',
  'positive',
  'negative',
] as const;
export const OUTSIDE_HOURS_TAGS = ['pre', 'after'] as const;

export const priceChangeSchema = z
  .object({
    status: z.enum(PRICE_CHANGE_STATUSES),
    change: z.string(),
    proportionChange: z.string(),
  })
  .strict();

export const outsideHoursSchema = z
  .object({
    label: z.string().min(1),
    tag: z.enum(OUTSIDE_HOURS_TAGS),
  })
  .strict();

export const priceDisplayPropsSchema = z
  .object({
    price: z.union([z.string(), z.number()]),
    currency: z.string().min(1),
    priceChange: priceChangeSchema.optional(),
    liveLabel: z.string().optional(),
    isPrimary: z.boolean().optional(),
    outsideHoursText: outsideHoursSchema.optional(),
    showMarketIndicator: z.boolean().optional(),
  })
  .strict();

export type OutsideTag = z.infer<typeof outsideHoursSchema>['tag'];
export type PriceChange = z.infer<typeof priceChangeSchema>;
export type OutsideHours = z.infer<typeof outsideHoursSchema>;
export type PriceDisplayDataProps = z.infer<typeof priceDisplayPropsSchema>;

export interface PriceDisplayProps extends PriceDisplayDataProps {
  timeNode?: ReactNode;
  className?: string;
}

export const PriceDisplay: FC<PriceDisplayProps> = ({
  price,
  currency,
  priceChange,
  liveLabel,
  timeNode,
  outsideHoursText,
  isPrimary = true,
  showMarketIndicator = true,
  className,
  ...rest
}) => (
  <div
    className={clsx(styles.container, className)}
    data-testid="module-price-display"
    data-price-display-wrapper
    {...rest}
  >
    <div
      className={clsx(styles.priceContainer, {
        [styles.isPrimary]: isPrimary,
      })}
      data-price-container
    >
      <div className={styles.priceAndCurrencyContainer}>
        <span className={styles.price} data-price>
          {price}
        </span>
        <span className={styles.currency} data-price-currency>
          {currency}
        </span>
      </div>
      {priceChange && (
        <div
          className={clsx(
            styles.priceChangeContainer,
            styles[priceChange.status]
          )}
          data-testid="price-change"
          data-price-change-container
        >
          <span className={styles.priceChangeLabel}>{priceChange.change}</span>
          <span className={styles.percentageChangeLabel}>
            {priceChange.proportionChange}
          </span>
        </div>
      )}
    </div>
    {showMarketIndicator && (
      <p className={styles.labelsContainer} data-testid="market-status">
        {timeNode}
        {outsideHoursText && (
          <span
            className={clsx(
              styles.outsideHoursTag,
              styles[outsideHoursText.tag]
            )}
            data-outside-hours-label
          >
            {outsideHoursText.label}
          </span>
        )}
        {liveLabel && (
          <span className={styles.liveLabel} data-live-label>
            {liveLabel}
          </span>
        )}
      </p>
    )}
  </div>
);
